import React, { useEffect, useState } from 'react';
import usePageTitle from 'hooks/usePageTitle';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import {
  Box,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useOutletContext, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { KpiCard, PagePaper } from 'dashboard/components/common';
import { fmtMoney } from 'utilities/format';
import ChecklistCard from './ChecklistCard';
import RevenueSparkline from './RevenueSparkline';
import RecentOrdersCard from './RecentOrdersCard';
import TopProductsCard from './TopProductsCard';

/**
 * Brand-owner home page. Pulls one report endpoint that returns KPIs,
 * a 30d revenue series, recent orders, top products, and a setup
 * checklist; sub-cards render each slice.
 */
export default function BrandOverview() {
  usePageTitle('Overview', 'How your brand is doing, and what is left to set up.');
  const [selectedBrand] = useOutletContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const accent = '#422AFB';

  // `cancelled` flag in cleanup suppresses state writes after unmount.
  // Doesn't abort the network call (the API helper doesn't pass a
  // signal yet) but eliminates the warning and the "ghost" re-render
  // StrictMode produces when it mounts twice.
  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.getBrandOverview({ brand_id: selectedBrand })
      .then((res) => { if (!cancelled) setData(res || null); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load overview', { toastId: 'overview-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand]);

  const kpis = data?.kpis;
  const currency = kpis?.currency || 'USD';

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <Stack spacing='20px'>
        {data?.checklist && data.checklist.done < data.checklist.total && (
          <ChecklistCard checklist={data.checklist} onJump={(p) => navigate(p)} />
        )}

        <Grid container spacing='16px'>
          {loading && !kpis && Array.from({ length: 4 }).map((_, i) => (
            <Grid key={i} item xs={12} sm={6} lg={3}>
              <Skeleton variant='rectangular' height={100} sx={{ borderRadius: '16px' }} />
            </Grid>
          ))}
          {kpis && (
            <>
              <Grid item xs={12} sm={6} lg={3}>
                <KpiCard
                  label='Orders today'
                  value={kpis.orders_today}
                  hint={`Revenue ${fmtMoney(kpis.revenue_today, currency)}`}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <KpiCard
                  label='Revenue MTD'
                  value={fmtMoney(kpis.revenue_mtd, currency)}
                  hint={`${kpis.orders_mtd} orders this month`}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <KpiCard
                  label='Pending orders'
                  value={kpis.pending_orders}
                  hint='Awaiting payment or review'
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={3}>
                <KpiCard
                  label='Customers'
                  value={kpis.customers}
                  hint='Total brand accounts'
                />
              </Grid>
            </>
          )}
        </Grid>

        <PagePaper sx={{ p: '18px', borderRadius: '16px' }}>
          <Stack direction='row' justifyContent='space-between' alignItems='center' mb='12px'>
            <Typography fontWeight='700'>Revenue · last 30 days</Typography>
            <Typography variant='caption' color='text.secondary'>
              {data?.revenue_30d?.[0]?.date} → {data?.revenue_30d?.[data?.revenue_30d?.length - 1]?.date}
            </Typography>
          </Stack>
          {loading && !data && <Skeleton variant='rectangular' height={80} />}
          {data && (
            <RevenueSparkline data={data.revenue_30d || []} accent={accent} />
          )}
        </PagePaper>

        <Grid container spacing='16px'>
          <Grid item xs={12} xl={6}>
            <RecentOrdersCard rows={data?.recent_orders} loading={loading} currency={currency} />
          </Grid>
          <Grid item xs={12} xl={6}>
            <TopProductsCard rows={data?.top_products} loading={loading} currency={currency} />
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
}
